'use client';

import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { usePathname as useNextIntlPathname, useRouter } from '@/i18n/navigation';
import { usePathname as useNextPathname, useSearchParams } from 'next/navigation';
import { useAppLocale, useMetadata } from '@/hooks';
import { getLocalizedValue } from '@/lib/utils';
import { CheckIcon, GlobeIcon } from 'lucide-react';
import Flag from 'react-flagkit';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { type LocaleType } from '@/types/locale';
import { type VehicleGroupType } from '@/types/vehicle-group';
import config from '@/config';
type PropsType = {
  locales: LocaleType[];
};
const LocaleSwitcher = ({
  locales
}: PropsType) => {
  const t = useTranslations();
  const {
    backendLocale
  } = useAppLocale();
  const {
    routeParams
  } = useMetadata();
  const router = useRouter();
  const canonicalPathname = useNextIntlPathname(); // e.g., /corporate (canonical internal route)
  const fullPathname = useNextPathname(); // e.g., /tr-tr/kurumsal (full URL with locale)
  const searchParams = useSearchParams();
  const isProcessingRef = useRef(false);

  /**
   * Convert a translated segment to its canonical route key
   */
  const getRouteKeyFromSegment = (segment: string, locale: string): string | null => {
    for (const [routeKey, translations] of Object.entries(routeParams)) {
      if (translations[locale] === segment) {
        return routeKey;
      }
    }
    return null;
  };

  /**
   * Translate a canonical route key to a target locale
   */
  const translateRouteKey = (routeKey_0: string, targetLocale: string): string => {
    return routeParams[routeKey_0]?.[targetLocale] || routeKey_0;
  };
  const handleLocaleChange = async (newLocale: string) => {
    // Prevent rapid double-clicks (debounce)
    if (isProcessingRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('⏸️ Locale change in progress, ignoring...');
      }
      return;
    }

    // Don't switch to the same locale
    if (backendLocale === newLocale) {
      return;
    }
    isProcessingRef.current = true;

    // Reset processing flag after timeout as fallback
    setTimeout(() => {
      isProcessingRef.current = false;
    }, 3000);
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Locale change:', {
        from: backendLocale,
        to: newLocale,
        canonicalPathname,
        fullPathname
      });
      console.log('📋 RouteParams:', routeParams);
    }

    // Extract path without a locale prefix from the fullPathname
    // e.g., /tr-tr/kurumsal -> /kurumsal
    const localePatternStr = locales.map(l => l.fullCode.toLowerCase()).join('|');
    const localePattern = new RegExp(`^/(${localePatternStr})`, 'i');
    let pathWithoutLocale = fullPathname.replace(localePattern, '') || '/';
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Path without locale:', pathWithoutLocale);
    }

    // If not homepage, translate the route segments
    if (pathWithoutLocale !== '/') {
      const segments = pathWithoutLocale.split('/').filter(Boolean);
      const translatedSegments: string[] = [];
      if (process.env.NODE_ENV === 'development') {
        console.log('📦 Segments to translate:', segments);
      }
      for (const segment_0 of segments) {
        // Step 1: Convert the current locale's translated segment to a canonical route key
        const routeKey_1 = getRouteKeyFromSegment(segment_0, backendLocale);
        if (routeKey_1) {
          // Step 2: Translate canonical key to target locale
          const translatedSegment = translateRouteKey(routeKey_1, newLocale);
          if (process.env.NODE_ENV === 'development') {
            console.log(`  ✅ "${segment_0}" (${backendLocale}) -> [${routeKey_1}] -> "${translatedSegment}" (${newLocale})`);
          }
          translatedSegments.push(translatedSegment);
        } else {
          // Not a translated route (dynamic slug), keep as-is
          if (process.env.NODE_ENV === 'development') {
            console.log(`  ⚠️ Not a translatable route, keeping as-is: "${segment_0}"`);
          }
          translatedSegments.push(segment_0);
        }
      }
      pathWithoutLocale = '/' + translatedSegments.join('/');
      if (process.env.NODE_ENV === 'development') {
        console.log('🎯 Final translated path:', pathWithoutLocale);
      }
    }

    // Translate query parameters (e.g. applications slugs)
    const translatedParams = new URLSearchParams(searchParams.toString());
    const applications = searchParams.get('applications');
    if (applications) {
      try {
        const res = await fetch(`${config.API_BASE_URL}/vehicle-groups`);
        if (res.ok) {
          const json = await res.json();
          const allGroups: VehicleGroupType[] = json?.data ?? json ?? [];
          const slugs = applications.split(',');
          const translatedSlugs = slugs.map(slug => {
            const vg = allGroups.find(v => getLocalizedValue(v, 'slug', backendLocale) === slug);
            return vg ? getLocalizedValue(vg, 'slug', newLocale) ?? slug : slug;
          });
          translatedParams.set('applications', translatedSlugs.join(','));
        }
      } catch {
        // fetch failed, keep original slugs
      }
    }
    const queryString = translatedParams.toString();
    const fullPath = queryString ? `${pathWithoutLocale}?${queryString}` : pathWithoutLocale;
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 Navigating to:', fullPath, 'with locale:', newLocale.toLowerCase());
      if (queryString) {
        console.log('🔗 Preserving query params:', queryString);
      }
    }

    // Use lowercase locale for URL
    // Scroll to top false to prevent jump and ensure query params are preserved
    router.replace(fullPath, {
      locale: newLocale.toLowerCase(),
      scroll: false
    });

    // Wait a bit for router.replace to process, then refresh to update locale context
    // Small delay ensures navigation completes before refresh
    await new Promise(resolve => setTimeout(resolve, 100));
    router.refresh();
  };
  const activeCode = backendLocale.split('-')[0].toUpperCase();
  return <DropdownMenu>
      <Tooltip>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <Button variant="ghost" className="hover:bg-brand-darken data-[state=open]:bg-brand-darken h-7! w-7! shrink-0 flex-col items-center p-0 hover:text-white focus-visible:outline-none md:h-9! md:w-9! lg:h-auto! lg:w-auto! lg:gap-0.5 lg:px-2 lg:py-1">
              <GlobeIcon className="size-4 md:size-5" />
              <span className="hidden text-[9px] leading-none font-medium opacity-80 lg:block">{activeCode}</span>
            </Button>
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <TooltipContent>
          <p>{t('language')}</p>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="z-60!">
        <DropdownMenuGroup>
          {locales.map(l_0 => <DropdownMenuItem key={l_0.fullCode} className={`flex flex-row justify-between gap-2 ${backendLocale === l_0.fullCode ? 'pointer-events-none cursor-default opacity-100!' : ''}`} onClick={() => handleLocaleChange(l_0.fullCode)}>
              <div className="flex flex-row items-center justify-start gap-2">
                <Flag country={l_0.countryCode} size={12} />
                <span>{l_0.name}</span>
              </div>
              {backendLocale === l_0.fullCode && <CheckIcon className="h-4 w-4" />}
            </DropdownMenuItem>)}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>;
};
export default LocaleSwitcher;